import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar, Box, Button, Chip, Paper, Snackbar, Alert, Stack, Tab, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import NotesIcon from '@mui/icons-material/Notes';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import type { ActionType, MyAction } from '../api/types';
import { useCompleteAction, useMyWork } from '../api/hooks';
import { EmptyState, ErrorState, LoadingState, StageChip, UserText } from '../components/common/Common';
import { CompleteActionDialog } from '../components/timeline/CompleteActionDialog';
import { MyActionDialog } from '../components/forms/MyActionDialog';
import { formatDate, formatDateTime } from '../components/common/format';

const FILTERS = ['all', 'overdue', 'today', 'week'] as const;

const ICONS: Record<ActionType, typeof CallIcon> = {
  call: CallIcon,
  email: EmailIcon,
  meeting: EventIcon,
  note: NotesIcon,
  task: TaskAltIcon,
};

export function MyActionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [filter, setFilter] = useState<string>('all');
  const { data, isLoading, error, refetch } = useMyWork(filter);
  const complete = useCompleteAction();
  const [completing, setCompleting] = useState<MyAction | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const actions = data?.actions ?? [];
  const nextSteps = data?.nextSteps ?? [];
  const overdueCount = actions.filter((a) => a.isOverdue).length + nextSteps.filter((n) => n.isOverdue).length;

  // Rows link through to where the action lives rather than editing it here: the my-work endpoint
  // returns a summary (no notes or outcome), so an edit form built from it would save those fields
  // back as null and quietly destroy them.
  const openAction = (action: MyAction) =>
    navigate(action.opportunityId
      ? `/opportunities/${action.opportunityId}`
      : `/customers/${action.customerId}`);

  const header = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: 2 }}
      flexWrap="wrap"
      useFlexGap
    >
      <Stack direction="row" alignItems="baseline" spacing={1.5}>
        <Typography variant="h1">{t('myActions.title')}</Typography>
        {overdueCount > 0 && (
          <Chip color="error" label={t('myActions.overdueCount', { count: overdueCount })} data-testid="overdue-count" />
        )}
      </Stack>
      <Button
        variant="contained"
        startIcon={isPhone ? undefined : <AddIcon />}
        onClick={() => setLogOpen(true)}
        data-testid="log-action"
        aria-label={t('myActions.logAction')}
      >
        {isPhone ? <AddIcon fontSize="small" /> : t('myActions.logAction')}
      </Button>
    </Stack>
  );

  if (isLoading) return <><Box>{header}</Box><LoadingState label={t('myActions.loading')} /></>;
  if (error) return <><Box>{header}</Box><ErrorState error={error} onRetry={() => void refetch()} /></>;

  return (
    <Box>
      {header}

      <Tabs
        value={filter}
        onChange={(_, v: string) => setFilter(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
      >
        {FILTERS.map((key) => (
          <Tab key={key} value={key} label={t(`myActions.filters.${key}`)} data-testid={`filter-${key}`} />
        ))}
      </Tabs>

      <Typography variant="h2" sx={{ mb: 1.5 }}>{t('myActions.openActions')}</Typography>
      {actions.length === 0 ? (
        <EmptyState title={t('myActions.nothingDue')} description={t('myActions.nothingDueDescription')} />
      ) : (
        <Stack spacing={1.5} data-testid="my-actions-list">
          {actions.map((a) => {
            const Icon = ICONS[a.type];
            return (
              <Paper
                key={a.id}
                variant="outlined"
                sx={{ p: 2, borderRadius: '10px', cursor: 'pointer' }}
                onClick={() => openAction(a)}
                data-testid={`action-${a.id}`}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    <Icon fontSize="small" />
                  </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h4"><UserText>{a.subject}</UserText></Typography>
                      <Chip size="small" variant="outlined" label={t(`actionType.${a.type}`)} />
                      {a.isOverdue && (
                        <Chip size="small" color="error" label={t('timeline.overdue')} data-testid={`action-overdue-${a.id}`} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      <UserText>{a.customerName}</UserText>
                      {a.opportunityTitle && <> · <UserText>{a.opportunityTitle}</UserText></>}
                      {a.dueAtUtc && ` · ${t('timeline.due', { date: formatDateTime(a.dueAtUtc) })}`}
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompleting(a);
                    }}
                    data-testid={`complete-action-${a.id}`}
                  >
                    {t('timeline.markComplete')}
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Typography variant="h2" sx={{ mt: 4, mb: 1.5 }}>{t('myActions.nextSteps')}</Typography>
      {nextSteps.length === 0 ? (
        <EmptyState title={t('myActions.noNextSteps')} description={t('myActions.noNextStepsDescription')} />
      ) : (
        <Stack spacing={1.5} data-testid="next-steps">
          {nextSteps.map((n) => (
            <Paper
              key={n.opportunityId}
              variant="outlined"
              sx={{ p: 2, borderRadius: '10px', cursor: 'pointer' }}
              onClick={() => navigate(`/opportunities/${n.opportunityId}`)}
              data-testid={`next-step-${n.opportunityId}`}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={<FlagIcon />}
                  label={formatDate(n.nextStepDueDate)}
                  color={n.isOverdue ? 'error' : 'default'}
                  variant={n.isOverdue ? 'filled' : 'outlined'}
                />
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                  <Typography variant="h4">
                    <UserText>{n.nextStep ?? t('myActions.noNextStepRecorded')}</UserText>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <UserText>{n.opportunityTitle}</UserText> · <UserText>{n.customerName}</UserText>
                  </Typography>
                </Box>
                <StageChip stage={n.stage} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <CompleteActionDialog
        action={completing ? { id: completing.id, subject: completing.subject } : null}
        onClose={() => setCompleting(null)}
        onConfirm={(outcome) => {
          if (completing) complete.mutate({ id: completing.id, outcome });
          setCompleting(null);
        }}
      />

      <MyActionDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSaved={() => setToast(t('myActions.created'))}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
